import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router";
import { useAuth } from "@/auth/AuthContext";
import { useApi } from "@/lib/api";
import { capitalizeFirst } from "@/lib/capitalizeFirst";
import { useCopyToClipboard } from "@/lib/useCopyToClipboard";
import { Button } from "./Button";
import { CommandBlock } from "./CommandBlock";
import { Modal, ModalTitle } from "./Modal";
import { TextInput } from "./TextInput";
import { Toast, useToast } from "./Toast";

interface CreateEntityModalProps {
  entityType: "connector" | "gateway";
}

export function CreateEntityModal({ entityType }: CreateEntityModalProps) {
  const { accessToken } = useAuth();
  const api = useApi();
  const { orgId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { toast, showToast } = useToast();
  const { copiedField, copyToClipboard } = useCopyToClipboard();
  const [showModal, setShowModal] = useState(false);
  const [modalView, setModalView] = useState<"form" | "revealed">("form");
  const [name, setName] = useState("");
  const [publicUrl, setPublicUrl] = useState("");
  const [creating, setCreating] = useState(false);
  const [revealedToken, setRevealedToken] = useState<string | null>(null);
  const [createdId, setCreatedId] = useState<string | null>(null);

  const endpoint = `/organizations/${orgId}/${entityType}s`;
  const isGateway = entityType === "gateway";

  useEffect(() => {
    if (searchParams.get("create") === "1") {
      // oxlint-disable-next-line react/set-state-in-effect
      setShowModal(true);
      setSearchParams(
        (current) => {
          const next = new URLSearchParams(current);
          next.delete("create");
          return next;
        },
        { replace: true },
      );
    }
  }, [searchParams, setSearchParams]);

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault();

    if (!accessToken || !orgId) {
      return;
    }

    setCreating(true);

    try {
      const body = await api.send<{ id: string; token: string }>(
        endpoint,
        "POST",
        isGateway ? { name, publicUrl } : { name },
        `Couldn't create ${entityType}.`,
      );

      setRevealedToken(body.token);
      setCreatedId(body.id);
      setModalView("revealed");
    } catch (err) {
      showToast(
        err instanceof Error ? err.message : `Couldn't create ${entityType}.`,
        "error",
      );
    } finally {
      setCreating(false);
    }
  }

  function handleDone() {
    setShowModal(false);
    setModalView("form");
    setRevealedToken(null);
    setCreatedId(null);
    setName("");
    setPublicUrl("");
  }

  function handleContinue() {
    const id = createdId;

    handleDone();

    if (id) {
      void navigate(`/${orgId}/networking/${entityType}s/${id}/details`);
    }
  }

  return (
    <>
      {showModal && (
        <Modal
          onClose={() => setShowModal(false)}
          closeOnBackdropClick={modalView !== "revealed"}
          closeOnEscape={modalView !== "revealed"}
          scrollable={false}
        >
          {modalView === "form" ? (
            <>
              <ModalTitle className="mb-4 shrink-0">
                Create a {entityType}
              </ModalTitle>
              <form
                onSubmit={handleCreate}
                className="flex min-h-0 flex-1 flex-col"
              >
                <div className="min-h-0 flex-1 space-y-4 overflow-y-auto pr-1">
                  <div>
                    <label
                      htmlFor="tokenName"
                      className="mb-1.5 block text-sm text-foreground"
                    >
                      Name
                    </label>
                    <TextInput
                      id="tokenName"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      required
                      autoFocus
                      className="w-full"
                    />
                  </div>
                  {isGateway && (
                    <div>
                      <label
                        htmlFor="gatewayPublicUrl"
                        className="mb-1.5 block text-sm text-foreground"
                      >
                        Public URL
                      </label>
                      <TextInput
                        type="url"
                        id="gatewayPublicUrl"
                        value={publicUrl}
                        onChange={(e) => setPublicUrl(e.target.value)}
                        placeholder="https://gateway.example.com:2112"
                        required
                        className="w-full"
                      />
                    </div>
                  )}
                </div>
                <div className="mt-4 flex shrink-0 justify-end gap-3">
                  <Button
                    onClick={() => setShowModal(false)}
                    variant="secondary"
                  >
                    Cancel
                  </Button>
                  <Button type="submit" disabled={creating} variant="primary">
                    {creating ? "Creating..." : `Create ${entityType}`}
                  </Button>
                </div>
              </form>
            </>
          ) : (
            <>
              <ModalTitle className="mb-4 shrink-0">
                {capitalizeFirst(entityType)} created
              </ModalTitle>
              <div className="min-h-0 flex-1 overflow-y-auto pr-1">
                <CommandBlock
                  className="mb-1.5"
                  command={revealedToken ?? ""}
                  field="token"
                  wrap
                  copiedField={copiedField}
                  onCopy={copyToClipboard}
                />
                <p className="text-xs text-foreground/70">
                  Copy this token now and store it securely. You won't be able
                  to view it again.
                </p>
              </div>

              <div className="mt-4 flex shrink-0 justify-end gap-3">
                <Button onClick={handleDone} variant="secondary">
                  Close
                </Button>
                <Button onClick={handleContinue} variant="primary">
                  Continue to {entityType}
                </Button>
              </div>
            </>
          )}
        </Modal>
      )}

      <Toast toast={toast} />
    </>
  );
}
